import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';
import { Phone, Zap, Tv, Droplets, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'KE', name: 'Kenya', currency: 'KES', flag: '🇰🇪' },
  { code: 'UG', name: 'Uganda', currency: 'UGX', flag: '🇺🇬' },
  { code: 'TZ', name: 'Tanzania', currency: 'TZS', flag: '🇹🇿' },
  { code: 'RW', name: 'Rwanda', currency: 'RWF', flag: '🇷🇼' },
  { code: 'NG', name: 'Nigeria', currency: 'NGN', flag: '🇳🇬' },
];

const UTILITY_ICONS = {
  electricity: <Zap className="w-5 h-5 text-yellow-500" />,
  water: <Droplets className="w-5 h-5 text-blue-500" />,
  tv: <Tv className="w-5 h-5 text-purple-500" />,
};

export default function ServicesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('KE');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingTransaction, setPendingTransaction] = useState(null);
  const [transactionResult, setTransactionResult] = useState(null);
  
  // Airtime form
  const [airtimeForm, setAirtimeForm] = useState({
    phone_number: '',
    amount: '',
    operator: ''
  });
  
  // Utility form
  const [utilityForm, setUtilityForm] = useState({
    account_number: '',
    amount: '',
    provider_code: ''
  });

  const token = localStorage.getItem('onepay_token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchOperators(selectedCountry);
    fetchProviders(selectedCountry);
  }, [selectedCountry, token, navigate]);

  const fetchOperators = async (country) => {
    try {
      const response = await axios.get(`${API_URL}/api/services/operators/${country}`);
      setOperators(response.data.operators || []);
    } catch (error) {
      console.error('Error fetching operators:', error);
    }
  };

  const fetchProviders = async (country) => {
    try {
      const response = await axios.get(`${API_URL}/api/services/utilities/${country}`);
      setProviders(response.data.providers || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const handleAirtimePurchase = () => {
    if (!airtimeForm.phone_number || !airtimeForm.amount || !airtimeForm.operator) {
      toast({ title: 'Error', description: 'Please fill all fields', variant: 'destructive' });
      return;
    }
    
    const country = COUNTRIES.find(c => c.code === selectedCountry);
    const operator = operators.find(o => o.code === airtimeForm.operator);
    
    setPendingTransaction({
      type: 'airtime',
      title: 'Buy Airtime',
      description: `${country.currency} ${airtimeForm.amount} airtime for ${airtimeForm.phone_number}`,
      details: {
        operator: operator?.name,
        phone: airtimeForm.phone_number,
        amount: airtimeForm.amount,
        currency: country.currency
      }
    });
    setShowConfirmDialog(true);
  };

  const handleUtilityPayment = () => {
    if (!utilityForm.account_number || !utilityForm.amount || !utilityForm.provider_code) {
      toast({ title: 'Error', description: 'Please fill all fields', variant: 'destructive' });
      return;
    }
    
    const country = COUNTRIES.find(c => c.code === selectedCountry);
    const provider = providers.find(p => p.code === utilityForm.provider_code);
    
    setPendingTransaction({
      type: 'utility',
      title: 'Pay Utility Bill',
      description: `${country.currency} ${utilityForm.amount} to ${provider?.name}`,
      details: {
        provider: provider?.name,
        account: utilityForm.account_number,
        amount: utilityForm.amount,
        currency: country.currency
      }
    });
    setShowConfirmDialog(true);
  };

  const executeTransaction = async () => {
    setShowConfirmDialog(false);
    setLoading(true);
    
    try {
      let response;
      
      if (pendingTransaction.type === 'airtime') {
        response = await axios.post(`${API_URL}/api/services/airtime`, {
          phone_number: airtimeForm.phone_number,
          amount: parseFloat(airtimeForm.amount),
          country_code: selectedCountry,
          operator: airtimeForm.operator
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${API_URL}/api/services/utility`, {
          account_number: utilityForm.account_number,
          amount: parseFloat(utilityForm.amount),
          country_code: selectedCountry,
          provider_code: utilityForm.provider_code
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      if (response.data.success) {
        setTransactionResult(response.data);
        toast({ title: 'Success!', description: response.data.message || 'Transaction completed' });
        // Reset forms
        setAirtimeForm({ phone_number: '', amount: '', operator: '' });
        setUtilityForm({ account_number: '', amount: '', provider_code: '' });
      } else {
        toast({ title: 'Error', description: response.data.error, variant: 'destructive' });
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Transaction failed', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
      setPendingTransaction(null);
    }
  };

  const country = COUNTRIES.find(c => c.code === selectedCountry);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="services-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Services</h1>
            <p className="text-muted-foreground">Buy airtime & pay bills across Africa</p>
          </div>
          
          {/* Country Selector */}
          <div className="w-full md:w-48">
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger data-testid="country-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map(c => (
                  <SelectItem key={c.code} value={c.code}>
                    <span className="flex items-center gap-2">
                      <span>{c.flag}</span>
                      <span>{c.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Success Result */}
        {transactionResult && (
          <Card className="border-teal-500 bg-teal-500/10">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-teal-500" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-teal-700 dark:text-teal-300">Transaction Successful</p>
                  <p className="text-sm text-muted-foreground">{transactionResult.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Reference: {transactionResult.reference_id}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setTransactionResult(null)}>
                  Dismiss
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs defaultValue="airtime" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:w-96">
            <TabsTrigger value="airtime" className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Airtime
            </TabsTrigger>
            <TabsTrigger value="utilities" className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Utilities
            </TabsTrigger>
          </TabsList>

          {/* Airtime Tab */}
          <TabsContent value="airtime">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Airtime Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-teal-500" />
                    Buy Airtime
                  </CardTitle>
                  <CardDescription>
                    Top up any mobile number in {country?.name}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Mobile Operator</Label>
                    <Select 
                      value={airtimeForm.operator} 
                      onValueChange={(v) => setAirtimeForm({...airtimeForm, operator: v})}
                    >
                      <SelectTrigger data-testid="operator-selector">
                        <SelectValue placeholder="Select operator" />
                      </SelectTrigger>
                      <SelectContent>
                        {operators.map(op => (
                          <SelectItem key={op.code} value={op.code}>{op.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      data-testid="airtime-phone-input"
                      placeholder="0712345678"
                      value={airtimeForm.phone_number}
                      onChange={(e) => setAirtimeForm({...airtimeForm, phone_number: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Amount ({country?.currency})</Label>
                    <Input
                      data-testid="airtime-amount-input"
                      type="number"
                      placeholder="100"
                      value={airtimeForm.amount}
                      onChange={(e) => setAirtimeForm({...airtimeForm, amount: e.target.value})}
                    />
                  </div>

                  {/* Quick amounts */}
                  <div className="flex flex-wrap gap-2">
                    {[50, 100, 200, 500, 1000].map(amt => (
                      <Button
                        key={amt}
                        variant="outline"
                        size="sm"
                        onClick={() => setAirtimeForm({...airtimeForm, amount: amt.toString()})}
                      >
                        {country?.currency} {amt}
                      </Button>
                    ))}
                  </div>

                  <Button 
                    data-testid="buy-airtime-btn"
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={handleAirtimePurchase}
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Buy Airtime
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              {/* Operators Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Available Networks</CardTitle>
                  <CardDescription>Supported operators in {country?.name}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {operators.map(op => (
                      <div key={op.code} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                        <div className="w-10 h-10 rounded-full bg-teal-500/20 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-teal-500" />
                        </div>
                        <div>
                          <p className="font-medium">{op.name}</p>
                          <p className="text-xs text-muted-foreground">Instant delivery</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Utilities Tab */}
          <TabsContent value="utilities">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Utility Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    Pay Utility Bill
                  </CardTitle>
                  <CardDescription>
                    Pay electricity, water, or TV subscriptions
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Service Provider</Label>
                    <Select 
                      value={utilityForm.provider_code} 
                      onValueChange={(v) => setUtilityForm({...utilityForm, provider_code: v})}
                    >
                      <SelectTrigger data-testid="provider-selector">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {providers.map(p => (
                          <SelectItem key={p.code} value={p.code}>
                            <span className="flex items-center gap-2">
                              {UTILITY_ICONS[p.type]}
                              {p.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Account/Meter Number</Label>
                    <Input
                      data-testid="utility-account-input"
                      placeholder="Enter account number"
                      value={utilityForm.account_number}
                      onChange={(e) => setUtilityForm({...utilityForm, account_number: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Amount ({country?.currency})</Label>
                    <Input
                      data-testid="utility-amount-input"
                      type="number"
                      placeholder="500"
                      value={utilityForm.amount}
                      onChange={(e) => setUtilityForm({...utilityForm, amount: e.target.value})}
                    />
                  </div>

                  <Button 
                    data-testid="pay-utility-btn"
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    onClick={handleUtilityPayment}
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Pay Bill
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              {/* Providers Grid */}
              <Card>
                <CardHeader>
                  <CardTitle>Available Services</CardTitle>
                  <CardDescription>Utility providers in {country?.name}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {providers.map(p => (
                      <div 
                        key={p.code} 
                        className="flex flex-col items-center p-4 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                        onClick={() => setUtilityForm({...utilityForm, provider_code: p.code})}
                      >
                        <div className="w-12 h-12 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center mb-2 shadow-sm">
                          {UTILITY_ICONS[p.type]}
                        </div>
                        <p className="text-sm font-medium text-center">{p.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{p.type}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Confirm Dialog */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{pendingTransaction?.title}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-4">
                  <p>{pendingTransaction?.description}</p>
                  {pendingTransaction?.details && (
                    <div className="bg-muted p-4 rounded-lg space-y-2 text-sm">
                      {Object.entries(pendingTransaction.details).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-muted-foreground capitalize">{key}:</span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={executeTransaction} className="bg-teal-600 hover:bg-teal-700">
                Confirm Payment
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
